import type { MemoKeyPoint, PreviousAnalysis } from '@ai-meeting/shared'
import { normalizeActions } from '@/utils/mapMemoToMeetingInsights'

/**
 * The live-memo frames, newest contract first.
 *
 * The AI service used to send the whole memo in one `summarize.result`. It now sends three frames,
 * each carrying a single pane and nothing else, which is why applying one is a merge rather than the
 * assignment it used to be: taking `key-points.result` as the memo would blank Main Content and
 * Confirm Action every time it landed.
 *
 * `summarize.result` is still listed. A client that only understood the three new frames would show
 * empty panes for a whole meeting if it met a server that had not been migrated yet, and the split
 * left no server-side flag to fall back on — so the tolerance has to live here.
 */
const MEMO_PANE_EVENTS = [
  'main-content.result',
  'key-points.result',
  'confirm-items.result',
  'summarize.result',
] as const

/** Shape both the proxy socket and the participant stream hand us. */
interface MemoFrame {
  event?: string
  data?: unknown
}

/** Whether this frame carries live-memo content, and so should drive memo state. */
export function isMemoPaneEvent(event: string | undefined): boolean {
  return event !== undefined && (MEMO_PANE_EVENTS as readonly string[]).includes(event)
}

function asStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null
  return value.filter((item): item is string => typeof item === 'string')
}

function asKeyPoints(value: unknown): MemoKeyPoint[] | null {
  if (!Array.isArray(value)) return null
  return value.filter((item): item is MemoKeyPoint => typeof item === 'object' && item !== null)
}

/**
 * Fold one live-memo frame into the memo built so far.
 *
 * A frame with no usable `data` returns `previous` untouched. That is the whole reason this returns
 * the previous value rather than null on a miss: the native path used to write
 * `setMemo(event.data ?? null)`, so a single frame that arrived without a body wiped a memo the
 * meeting had spent twenty minutes accumulating.
 *
 * The three pane frames merge — each writes only its own fields. `summarize.result` replaces,
 * because that is what it always meant: it is one analysis of the whole meeting, and a field it
 * leaves out is a field the server no longer believes in. Preserving that difference is what lets
 * both contracts be live at once without either corrupting the other's output.
 *
 * Actions — and only actions — go through `normalizeActions` on the way in. This is the boundary
 * where a socket frame becomes state for all three paths (proxy, native, room replay), and since
 * BRAI-549 an action can carry a `conflict` block whose `link` is rendered as an href; validating it
 * anywhere later would mean validating it three times. The other panes are passed through as they
 * always were, so this change cannot alter what they render.
 *
 * @param previous The memo assembled from earlier frames, or null before the first one.
 * @param frame    The inbound socket frame, verbatim.
 * @returns The memo to display now — `previous` itself when the frame changes nothing.
 */
export function applyMemoPane(
  previous: PreviousAnalysis | null,
  frame: MemoFrame
): PreviousAnalysis | null {
  if (!isMemoPaneEvent(frame.event)) return previous

  const data = frame.data
  if (!data || typeof data !== 'object') return previous
  const record = data as Record<string, unknown>

  if (frame.event === 'summarize.result') {
    return {
      main_content: asStringArray(record.main_content) ?? [],
      key_points: asKeyPoints(record.key_points) ?? [],
      summary: typeof record.summary === 'string' ? record.summary : '',
      actions: normalizeActions(record.actions),
    }
  }

  // Built fresh rather than spread from `EMPTY_PREVIOUS_ANALYSIS`: a shallow copy of that const
  // hands out its own arrays, so anything that later sorted or pushed in place would be editing
  // the shared empty for the whole app.
  const base: PreviousAnalysis = previous ?? {
    main_content: [],
    key_points: [],
    summary: '',
    actions: [],
  }

  if (frame.event === 'main-content.result') {
    const mainContent = asStringArray(record.main_content)
    return mainContent ? { ...base, main_content: mainContent } : previous
  }

  if (frame.event === 'key-points.result') {
    const keyPoints = asKeyPoints(record.key_points)
    const summary = typeof record.summary === 'string' ? record.summary : null
    if (!keyPoints && summary === null) return previous
    return {
      ...base,
      ...(keyPoints ? { key_points: keyPoints } : {}),
      ...(summary === null ? {} : { summary }),
    }
  }

  if (!Array.isArray(record.actions)) return previous
  return { ...base, actions: normalizeActions(record.actions) }
}
